import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { APP_STORE_URL, PLAY_STORE_URL, buildAndroidIntentUrl } from '@breath-lab/core';
import Button from '../Button/Button';
import { getMobilePlatform, isAndroidDevice } from '../../utils/platform';
import styles from './AppInstallBanner.module.css';

const STORAGE_KEY = 'app_install_banner_dismissed';

interface AppInstallBannerProps {
  /** Fired once when the bar becomes visible. */
  onView?: () => void;
  /** Fired when the CTA is tapped. */
  onCtaClick?: () => void;
  /** Fired when the bar is dismissed. */
  onDismiss?: () => void;
}

/**
 * Dismissible "open the native app" bar for mobile web visitors.
 *
 *   Android (Chrome etc.) → CTA fires an intent:// URL that opens the app if
 *                           installed, else falls back to the Play Store.
 *                           In-app webviews, where intent:// is unreliable,
 *                           link straight to the Play Store.
 *   iOS (Safari / Chrome / in-app) → CTA links to the App Store listing.
 *   desktop → renders nothing.
 *
 * We render our own bar on every mobile browser rather than relying on Apple's
 * native Smart App Banner, which only shows in Safari, only on the production
 * https origin, and never in Chrome-iOS.
 *
 * Mounted inside the page layouts (next to BetaBanner), so the bare-routed
 * player / success / welcome screens never receive it — no route conditionals.
 */
export default function AppInstallBanner({ onView, onCtaClick, onDismiss }: AppInstallBannerProps) {
  const { t } = useTranslation('common');
  const [platform] = useState(() => getMobilePlatform());
  const [intentCapable] = useState(() => isAndroidDevice());
  const [dismissed, setDismissed] = useState(() => localStorage.getItem(STORAGE_KEY) === 'true');

  useEffect(() => {
    if (platform && !dismissed) {
      onView?.();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!platform || dismissed) return null;

  const href =
    platform === 'ios'
      ? APP_STORE_URL
      : intentCapable
        ? buildAndroidIntentUrl()
        : PLAY_STORE_URL;

  function handleDismiss() {
    localStorage.setItem(STORAGE_KEY, 'true');
    setDismissed(true);
    onDismiss?.();
  }

  return (
    <div className={styles.banner} role="region" aria-label={t('appBanner.ariaLabel')}>
      <div className={styles.inner}>
        <img className={styles.icon} src="/app_icon.png" alt="" aria-hidden="true" />
        <p className={styles.text}>{t('appBanner.text')}</p>
        <Button as="a" href={href} size="md" className={styles.cta} onClick={onCtaClick}>
          {t('appBanner.cta')}
        </Button>
        <button
          type="button"
          className={styles.dismissButton}
          onClick={handleDismiss}
          aria-label={t('dismiss')}
        >
          ✕
        </button>
      </div>
    </div>
  );
}
